/*
 * Copyright (c) 2021 SARL SIGMA POLYNESIA
 */

import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck'
import { faSpinner } from '@fortawesome/free-solid-svg-icons/faSpinner'
import { faTimes } from '@fortawesome/free-solid-svg-icons/faTimes'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useRef, useState } from 'react'
import { FormattedList, FormattedMessage } from 'react-intl'
import { randomId } from '../../../modules/twh/lib/random.mjs'
import FormattedFileSize from './FormattedFileSize'
import { Alert, Box, BoxProps, Group, Stack, Text } from '@mantine/core'
import { faDownload } from '@fortawesome/free-solid-svg-icons/faDownload'

type UploadIconProps = {
  file: {
    failed: boolean,
    uploading: boolean
  }
}

function UploadIcon (props: UploadIconProps) {
  const { file } = props

  if (file.failed) {
    return (
      <FontAwesomeIcon
        fixedWidth
        icon={faTimes}
      />
    )
  }
  if (file.uploading) {
    return (
      <FontAwesomeIcon
        fixedWidth
        icon={faSpinner}
        spin
      />
    )
  }
  return (
    <FontAwesomeIcon
      fixedWidth
      icon={faCheck}
    />
  )
}

export type DragAndDropAreaProps = BoxProps & {
  accept?: string[],
  onUpload (file: File): Promise<unknown>,
}

type FileUpload = {
  key: string,
  name: string,
  size: number,
  type: string,
  failed: boolean,
  uploading: boolean,
}

function DragAndDropArea (props: DragAndDropAreaProps) {
  const {
    accept,
    onUpload,
    ...others
  } = props

  const inputRef = useRef()
  const [dragging, setDragging] = useState<boolean>(false)
  const [files, setFiles] = useState([])
  const [error, setError] = useState<Error>()

  const uploadFile = useCallback((file: File) => {
    const item: FileUpload = {
      key: randomId(),
      name: file.name,
      size: file.size,
      type: file.type,
      failed: false,
      uploading: true
    }
    setFiles((s) => [...s, item])

    onUpload(file)
      .then(() => {
        item.uploading = false
        setFiles((s) => s.filter((el) => el.key !== item.key))
      })
      .catch((error: Error) => {
        setError(error)
        item.uploading = false
        item.failed = true
        setFiles((s) => [...s])
      })
  }, [onUpload])

  const handleDragEnter = useCallback(() => {
    setDragging(true)
  }, [])

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
  }, [])

  const handleDragExit = useCallback(() => {
    setDragging(false)
  }, [])

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    const { files: items } = event.dataTransfer

    for (let i = 0; i < items.length; i += 1) {
      const item = items[i]
      // Check MIME type.
      if (accept.indexOf(item.type) !== -1) {
        uploadFile(item)
      }
    }
    setDragging(false)
  }, [accept, uploadFile])

  const handleSelectFile = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { files: items } = event.target

    for (let i = 0; i < items.length; i += 1) {
      const item = items[i]
      // Check MIME type.
      if (accept.indexOf(item.type) !== -1) {
        uploadFile(item)
      }
    }
  }, [accept, uploadFile])

  return (
    <Box
      bg={dragging ? 'primary' : 'gray.0'}
      ta="center"
      p="md"
      c="dimmed"
      {...others}
      onDragEnter={handleDragEnter}
      onDragExit={handleDragExit}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <label htmlFor="file-loader">
        <input
          id="file-loader"
          multiple
          onChange={handleSelectFile}
          ref={inputRef}
          style={{ display: 'none' }}
          type="file"
        />
        <Stack gap="xs" align="center">
          <FontAwesomeIcon
            fixedWidth
            icon={faDownload}
            size="3x"
          />
          <div>
            <FormattedMessage
              id="drop_files_here_or_browse_files"
              defaultMessage="Déposez des fichiers ici ou sélectionnez des fichiers"
            />
          </div>
          {accept != null && accept.length > 0
            ? <Text size="sm">
              <FormattedMessage
                id="accepted_file_types"
                defaultMessage="Fichiers acceptés: {types}"
                values={{
                  types:
                    <FormattedList value={accept.map((mime) => mime.split(/\//g)[1].toUpperCase())} />
                }}
              />
            </Text>
            : null}
        </Stack>
      </label>

      {error
        ? <Alert color="red">{error.message}</Alert>
        : null}

      {files.length > 0
        ? <Stack mt="md">
          {files.map((file, index) => (
            <Group key={file.key} fz="sm">
              <Text>{index + 1}</Text>
              <Text>{file.name}</Text>
              <Text>{file.type}</Text>
              <Text><FormattedFileSize value={file.size} /></Text>
              <Text>
                <UploadIcon file={file} />
                {/* todo add button to delete file */}
              </Text>
            </Group>
          ))}
        </Stack>
        : null}
    </Box>
  )
}

export default DragAndDropArea
